import type { AuthError } from '@supabase/supabase-js';

/**
 * Convert a Supabase AuthError (or anything thrown by the client) into a
 * short, user-facing sentence. The Supabase error messages are usually
 * already reasonable, but a few common cases are worth re-phrasing.
 */
export function describeAuthError(error: unknown): string {
  if (!error) return 'Something went wrong. Try again.';

  const supabaseError = error as Partial<AuthError> & { message?: string; status?: number };
  const message = (supabaseError.message ?? '').toLowerCase();
  const status = supabaseError.status;

  if (message.includes('invalid login credentials')) {
    return 'Email or password is incorrect.';
  }
  if (message.includes('email not confirmed')) {
    return 'Confirm your email before signing in. Check your inbox.';
  }
  if (message.includes('email rate limit') || message.includes('over_email_send_rate_limit')) {
    return 'Too many emails sent. Wait a minute and try again.';
  }
  if (message.includes('user already registered') || message.includes('already been registered')) {
    return 'That email is already enlisted. Try signing in instead.';
  }
  if (message.includes('password should be') || message.includes('weak password')) {
    return 'Password is too weak. Use at least 8 characters including a number.';
  }
  if (message.includes('signup is disabled')) {
    return 'New signups are temporarily disabled. Try again later.';
  }
  if (status === 429) {
    return 'Too many attempts. Wait a minute and try again.';
  }

  return supabaseError.message ?? 'Something went wrong. Try again.';
}
